
import { computed, defineComponent, ref, watch, onMounted } from "vue-demi";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import { SMDocumentsV2, SMTasks, SMUser } from "@/store";
import pdf from "@teckel/vue-pdf";

export default defineComponent({
  name: "RTWCStep2",
  components: {
    ActionButtons: () => import("@/components/ActionContainer/ActionButtonsV2.vue"),
    ActionContainerCard: () => import("@/components/ActionContainer/ActionContainerCard.vue"),
    pdf,
    Notification: () => import("@/modules/RTWC/notification/Notification.vue"),
    PopupIdentityMismatch: () => import("@/modules/RTWC/employer/PopupIdentityMismatch.vue"),
    PopupDecline: () => import("@/modules/RTWC/employer/PopupDecline.vue"),
  },
  props: {
    fileId: {
      type: Number,
      default: 0,
    },
    started: {
      type: Boolean,
      default: false,
    },
    completed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["restart"],
  setup(props) {
    const loading = ref(false);

    const daysLeft = ref(undefined);

    const hasActions = computed(() => {
      if (SMUser.isEmployee) return false;

      return !props.completed;
    });

    onMounted(async () => {
      try {
        loading.value = true;

        const { data } = await axios.get(
          API_ROUTES.COMPLIANCE_RTWC_VALIDATE(SMTasks.taskTemplate.id)
        );

        if (!data) return;

        daysLeft.value = data.days_left;
      } finally {
        loading.value = false;
      }
    });

    const isLessThan10Days = computed<boolean>(
      () => daysLeft.value != undefined && daysLeft.value <= 10
    );

    const isConfirmedPhotograph = ref(false);
    const isConfirmedDownloaded = ref(false);

    const isConfirmedExpiry = ref(false);

    const isDisabled = computed(() => {
      if (isLessThan10Days.value)
        return (
          !isConfirmedPhotograph.value ||
          !isConfirmedDownloaded.value ||
          loading.value ||
          !isConfirmedExpiry.value
        );

      return !isConfirmedPhotograph.value || !isConfirmedDownloaded.value || loading.value;
    });

    const okLabel = computed(() =>
      isLessThan10Days.value ? "Proceed & Start ECS Check" : "Complete"
    );

    async function complete(res: "accept" | "ecs" | "decline") {
      let result: "accept" | "ecs" | "decline" = isLessThan10Days.value ? "ecs" : "accept";

      if (res) result = res;

      try {
        loading.value = true;

        await axios.get(API_ROUTES.COMPLIANCE_RTWC_FINISH(SMTasks.taskTemplate.id, result));

        SMTasks.openSuccessPopup();
      } finally {
        loading.value = false;
      }
    }

    const preview = ref({
      link: "",
      name: "",
      currentPage: 1,
      pageCount: 1,
      zoom: 100,
      minZoom: 20,
      maxZoom: 100,
    });

    watch(
      () => props.fileId,
      async (fileId) => {
        if (!fileId) return;

        try {
          const file = await SMDocumentsV2.getFileData(fileId);

          if (file && typeof file.link === "string") {
            preview.value.link = file.link;
          } else {
            preview.value.link = "";
          }
          preview.value.name = file.name || "Unnamed File";

          Object.assign(preview.value, file);
        } catch (error) {
          console.error("Failed to load file data:", error);
        }
      },
      { deep: true, immediate: true }
    );

    function setPageCount(payload) {
      preview.value.pageCount = payload;
    }

    function setCurrentPage(payload) {
      preview.value.currentPage = payload;
    }

    const computedPreviewStyle = computed(() => {
      return {
        width: `${preview.value.zoom}%`,
        margin: "0 auto",
        borderRadius: "10px",
      };
    });

    function downloadFile() {
      const { link, name } = preview.value;

      SMDocumentsV2.downloadFile({ link, name });
    }

    const isIdentityMismatchPopupOpen = ref(false);

    function toggleIdentityMismatchPopup(value: boolean) {
      isIdentityMismatchPopupOpen.value = value;
    }

    function onIdentityMismatchComplete() {
      complete("decline");
    }

    function onIdentityMismatchCancel() {
      //
    }

    const isDeclinePopupOpen = ref(false);

    function toggleDeclinePopup(value: boolean) {
      isDeclinePopupOpen.value = value;
    }

    function onDeclineComplete() {
      complete("decline");
    }

    function onDeclineCancel() {
      //
    }

    return {
      loading,
      okLabel,
      isConfirmedPhotograph,
      isConfirmedDownloaded,
      isConfirmedExpiry,
      isDisabled,
      isLessThan10Days,
      complete,
      preview,
      setPageCount,
      setCurrentPage,
      computedPreviewStyle,
      downloadFile,
      isIdentityMismatchPopupOpen,
      toggleIdentityMismatchPopup,
      onIdentityMismatchComplete,
      onIdentityMismatchCancel,
      isDeclinePopupOpen,
      toggleDeclinePopup,
      onDeclineComplete,
      onDeclineCancel,
      hasActions,
    };
  },
});
